import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Chip, FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    return [...state, ...action.payload];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  }
}));

const truncate = (text, length = 60) => {
  if (!text) return "";
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

const MessageApiQueue = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState("");
  const [messages, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    if (!user.super) {
      toast.error(
        "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
      );
      setTimeout(() => {
        history.push(`/`);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, status]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/message-api-queue", {
            params: { searchParam, status, pageNumber }
          });
          dispatch({ type: "LOAD_MESSAGES", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchMessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, status, pageNumber]);

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleStatusChange = event => {
    setStatus(event.target.value);
  };

  const loadMore = () => {
    setPageNumber(prevPageNumber => prevPageNumber + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("messageApiQueue.title")}</Title>
        <MainHeaderButtonsWrapper>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">
              {i18n.t("messageApiQueue.filters.status")}
            </InputLabel>
            <Select
              labelId="status-filter-label"
              value={status}
              onChange={handleStatusChange}
              label={i18n.t("messageApiQueue.filters.status")}
            >
              <MenuItem value="">{i18n.t("messageApiQueue.filters.all")}</MenuItem>
              <MenuItem value="pending">{i18n.t("messageApiQueue.filters.pending")}</MenuItem>
              <MenuItem value="sent">{i18n.t("messageApiQueue.filters.sent")}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              )
            }}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("messageApiQueue.table.id")}</TableCell>
              <TableCell align="center">{i18n.t("messageApiQueue.table.company")}</TableCell>
              <TableCell align="center">{i18n.t("messageApiQueue.table.connection")}</TableCell>
              <TableCell align="center">{i18n.t("messageApiQueue.table.destination")}</TableCell>
              <TableCell align="center">{i18n.t("messageApiQueue.table.body")}</TableCell>
              <TableCell align="center">{i18n.t("messageApiQueue.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("messageApiQueue.table.schedule")}</TableCell>
              <TableCell align="center">{i18n.t("messageApiQueue.table.createdAt")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {messages.map(message => (
                <TableRow key={message.id}>
                  <TableCell align="center">{message.id}</TableCell>
                  <TableCell align="center">{message.company?.name}</TableCell>
                  <TableCell align="center">{message.whatsapp?.name}</TableCell>
                  <TableCell align="center">
                    {message.contact?.name || message.number}
                    {message.contact?.isGroup && (
                      <Chip
                        label={i18n.t("messageApiQueue.table.group")}
                        size="small"
                        style={{ marginLeft: 4 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">{truncate(message.body)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={
                        message.isSending
                          ? i18n.t("messageApiQueue.filters.sent")
                          : i18n.t("messageApiQueue.filters.pending")
                      }
                      size="small"
                      color={message.isSending ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {message.schedule && new Date(message.schedule).toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell align="center">
                    {message.createdAt && new Date(message.createdAt).toLocaleString("pt-BR")}
                  </TableCell>
                </TableRow>
              ))}

              {loading &&
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRowSkeleton key={`skeleton-${idx}`} columns={8} />
                ))}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default MessageApiQueue;
